/**
 * This file provides a reference to the Redux store without creating circular dependencies.
 * It exports a setStore function which is called in store/index.ts after the store is created.
 */

import { StoreWithReducerManager } from '../dynamicStore';
import { Persistor } from 'redux-persist';

// Store reference to be set after creation
let storeRef: StoreWithReducerManager | null = null;
let persistorRef: Persistor | null = null;

// Persistor subscribers (observer pattern)
type PersistorSubscriber = (persistor: Persistor) => void;
const persistorSubscribers: Set<PersistorSubscriber> = new Set();

/**
 * Set the Redux store reference
 * This is called from store/index.ts after the store is created
 */
export const setStore = (store: StoreWithReducerManager) => {
  storeRef = store;
};

/**
 * Set the Redux persistor reference
 * This is called from store/index.ts after the persistor is created
 * Notifies all subscribers when persistor becomes available
 */
export const setPersistor = (persistor: Persistor) => {
  persistorRef = persistor;

  // Notify all subscribers
  persistorSubscribers.forEach(callback => callback(persistor));

  // Clear subscribers after notification (one-time use)
  persistorSubscribers.clear();
};

/**
 * Get the Redux store
 * Returns the store reference, throwing an error if it hasn't been initialized
 */
export const getStore = (): StoreWithReducerManager => {
  if (!storeRef) {
    throw new Error('Store not initialized. Make sure setStore is called before using getStore.');
  }
  return storeRef;
};

/**
 * Get the Redux persistor
 * Returns the persistor reference, throwing an error if it hasn't been initialized
 */
export const getPersistor = (): Persistor => {
  if (!persistorRef) {
    throw new Error(
      'Persistor not initialized. Make sure setPersistor is called before using getPersistor.',
    );
  }
  return persistorRef;
};

/**
 * Subscribe to persistor creation
 * If persistor already exists, callback is called immediately
 * Returns an unsubscribe function for cleanup
 *
 * @param callback - Function to call when persistor is available
 * @returns Unsubscribe function to remove the subscription
 */
export const subscribeToPersistor = (callback: PersistorSubscriber): (() => void) => {
  // If persistor already exists, call immediately
  if (persistorRef) {
    callback(persistorRef);
    return () => {}; // No-op unsubscribe
  }

  // Otherwise, add to subscribers
  persistorSubscribers.add(callback);

  // Return unsubscribe function
  return () => {
    persistorSubscribers.delete(callback);
  };
};

/**
 * Update persistence when new reducers are added
 */
export const updatePersistence = () => {
  console.log('🔄 Updating persistence configuration...');
  if (persistorRef) {
    // Force the persistor to persist the current state
    persistorRef.flush();
  }
};
